import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';

import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { PageScrollSpyComponent, ScrollSpySection } from '../../../shared/components/page-scroll-spy/page-scroll-spy.component';
import { SelectPesquisavelComponent } from '../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';

import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { ContasReceberParcelaService } from '../../../shared/services/apis/financas/contas-receber-parcela.service';
import { ContasReceberParcelaGetByIdResponse } from '@shared/services/responses/financas/contas-receber-parcela-get-by-id.response';
import { ContasReceberService } from '../../../shared/services/apis/financas/contas-receber.service';
import { PessoaService } from '../../../shared/services/apis/pessoa/pessoa.service';
import { MunicipioService } from '../../../shared/services/apis/usuario/municipio.service';
import { SacadoBoletoStruct } from '../../../shared/services/structs/financas/sacado-boleto.struct';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MaskService } from '../../../shared/services/mask.service';
import { AbntPipe } from '../../../shared/pipes/abnt.pipe';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../../../environments/environment';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';

export interface ContasReceberParcelaAnexoStruct {
    idContasReceberParcelaAnexo?: number;
    nomeArquivo: string;
    tamanhoBytes: number;
    urlArquivo?: string;
    base64?: string;
}

@Component({
    selector: 'app-contas-receber-parcela-cadastro',
    standalone: true,
    imports: [
        AlertCardComponent,
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatSlideToggleModule,
        MatTooltipModule,
        NgxCurrencyDirective,
        HeaderComponent,
        FooterActionComponent,
        LoadingBoxComponent,
        PageScrollSpyComponent,
        SelectPesquisavelComponent,
        AbntPipe,
        ConfirmModalComponent
    ],
    templateUrl: './contas-receber-parcela-cadastro.component.html',
    styleUrl: './contas-receber-parcela-cadastro.component.css'
})
export class ContasReceberParcelaCadastroComponent implements OnInit {

    public moduloEnum = ModuloEnum.Financas;
    public funcionalidadeEnum = FuncionalidadeEnum.Financas_Contas_Receber;

    public form!: FormGroup;
    public isLoading: boolean = false;
    public isLoadingLookups: boolean = false;
    public isSaving: boolean = false;

    public idContasReceberParcela: number | null = null;
    public currencyMask: any;

    // Dados do documento pai (readonly, exibição no header)
    public idContasReceber: number = 0;
    public nomeCliente: string = '';
    public descricaoOriginal: string = '';
    public valorTotalDocumento: number = 0;
    public qtdParcelas: number = 0;
    public nroParcela: number | null = null;
    public idCategoriaFinanceira: number = 0;
    public idPessoaCliente: number = 0;
    public statusDescricao: string | null = null;

    // Controle de edição
    public podeEditarVencimento: boolean = true;
    /** Pedido de venda que gerou o documento: valor, vencimento, forma e conta ficam travados (o resto segue editável). */
    public idVenda: number | null = null;
    /** NF-e de saída que gerou o documento: os mesmos campos travados do pedido de venda, alterados só pela nota. */
    public idNFeSaida: number | null = null;

    /** Taxa e tarifa do intermediador previstas pelo pedido de venda (T07): só exibidas quando houver. */
    public get temPrevistosDoIntermediador(): boolean {
        return (this.form.get('valorTaxaIntermediadorPrevisto')?.value || 0) > 0 || (this.form.get('valorTarifaIntermediadorPrevisto')?.value || 0) > 0;
    }

    // Liquidações
    public liquidacoes: any[] = [];

    // Anexos da parcela
    public anexos: ContasReceberParcelaAnexoStruct[] = [];
    public readonly MAX_FILE_SIZE = 2 * 1024 * 1024;
    public readonly MAX_TOTAL_SIZE = 10 * 1024 * 1024;
    public isHovering: boolean = false;

    // Lookups
    public listaFormaRecebimento: any[] = [];
    public listaContaFinanceira: any[] = [];
    public listaTags: any[] = [];

    private _originalValor: number | null = null;
    private _originalJurosPrevisto: number | null = null;
    private _originalMultaPrevisto: number | null = null;
    private _originalAcrescimoPrevisto: number | null = null;
    private _originalIdContaFinanceira: number | null = null;

    // Scroll Spy
    get sections(): ScrollSpySection[] {
        const s: ScrollSpySection[] = [
            { id: 'dados-parcela', label: 'Dados da Parcela' },
            { id: 'previsao', label: 'Previsão de Recebimento' },
            { id: 'complementos', label: 'Complementos' }
        ];
        if (this.liquidacoes.length > 0) {
            s.splice(2, 0, { id: 'resumo-baixa', label: 'Resumo da Baixa' });
        }
        return s;
    }

    get idFormaRecebimentoControl(): FormControl {
        return this.form.get('idFormaRecebimento') as FormControl;
    }

    get idContaFinanceiraControl(): FormControl {
        return this.form.get('idContaFinanceira') as FormControl;
    }

    get tagsControl(): FormControl {
        return this.form.get('tags') as FormControl;
    }

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private contasReceberParcelaService: ContasReceberParcelaService,
        private contasReceberService: ContasReceberService,
        private pessoaService: PessoaService,
        private municipioService: MunicipioService,
        private alertService: AlertService,
        private maskService: MaskService,
        private dialog: MatDialog
    ) {
        this.currencyMask = this.maskService.getDynamicCurrencyMask();
    }

    ngOnInit(): void {
        this.initForm();
        this.carregarLookups();
        this.checkRouteParams();
    }

    private initForm(): void {
        this.form = this.fb.group({
            valor: [null],
            dataVencimento: ['', Validators.required],
            datetimePrevisao: [''],
            descricao: [''],
            idFormaRecebimento: [null],
            idContaFinanceira: [null],
            valorJurosPrevisto: [0],
            valorMultaPrevisto: [0],
            valorDescontoPrevisto: [0],
            valorAcrescimoPrevisto: [0],
            valorTaxaIntermediadorPrevisto: [{ value: 0, disabled: true }],
            valorTarifaIntermediadorPrevisto: [{ value: 0, disabled: true }],
            observacoesHtml: [''],
            nsuTid: [''],
            tags: [[]]
        });
    }

    private carregarLookups(): void {
        this.isLoadingLookups = true;
        this.contasReceberService.getLookups().subscribe({
            next: (res) => {
                this.isLoadingLookups = false;
                if (!res.isError) {
                    this.listaFormaRecebimento = res.formasRecebimento || [];
                    this.listaContaFinanceira = res.contasFinanceiras || [];
                    this.listaTags = res.tags || [];
                }
            },
            error: () => {
                this.isLoadingLookups = false;
                this.alertService.show('Erro', 'Não foi possível carregar as listas de seleção.', AlertType.error);
            }
        });
    }

    get rotaVoltar(): any[] {
        if (this.idContasReceber) {
            return ['/financas/contas-receber/cadastrar', { idContasReceber: this.idContasReceber }];
        }
        return ['/financas/contas-receber'];
    }

    private checkRouteParams(): void {
        const paramId = this.route.snapshot.paramMap.get('idContasReceberParcela');
        const queryidContasReceber = this.route.snapshot.queryParamMap.get('idContasReceber');
        if (queryidContasReceber) {
            this.idContasReceber = Number(queryidContasReceber);
        }
        if (paramId) {
            this.idContasReceberParcela = Number(paramId);
            this.loadData();
        } else {
            this.alertService.show('Erro', 'Parcela não informada.', AlertType.error);
            this.router.navigate(['/financas/contas-receber']);
        }
    }

    private loadData(): void {
        if (!this.idContasReceberParcela) return;

        this.isLoading = true;
        this.contasReceberParcelaService.getById(this.idContasReceberParcela).subscribe({
            next: (res: ContasReceberParcelaGetByIdResponse) => {
                this.isLoading = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Erro ao carregar a parcela.', AlertType.error);
                    this.router.navigate(['/financas/contas-receber']);
                    return;
                }

                // Dados do documento pai
                this.idContasReceber = res.idContasReceber;
                this.idPessoaCliente = res.idPessoaCliente;
                this.nomeCliente = res.nomeCliente || '';
                this.descricaoOriginal = res.descricaoOriginal || '';
                this.valorTotalDocumento = res.valorTotalDocumento || 0;
                this.qtdParcelas = res.qtdParcelas || 0;
                this.idCategoriaFinanceira = res.idCategoriaFinanceira || 0;
                this.nroParcela = res.nroParcela;
                this.statusDescricao = res.statusDescricao;
                this.podeEditarVencimento = res.podeEditarVencimento;
                this.idVenda = res.idVenda ?? null;
                this.idNFeSaida = res.idNFeSaida ?? null;
                if (this.idVenda || this.idNFeSaida) {
                    ['valor', 'dataVencimento', 'idFormaRecebimento', 'idContaFinanceira']
                        .forEach(campo => this.form.get(campo)?.disable());
                }

                // Liquidações
                this.liquidacoes = res.liquidacoes || [];

                // Anexos
                this.anexos = (res.anexos || []).map(a => ({
                    idContasReceberParcelaAnexo: a.idContasReceberParcelaAnexo,
                    nomeArquivo: a.nomeArquivo || 'arquivo',
                    tamanhoBytes: a.tamanhoBytes || 0,
                    urlArquivo: a.urlArquivo
                }));

                // Popular formulário
                this.form.patchValue({
                    valor: res.valor,
                    dataVencimento: res.datetimeVencimento ? new Date(res.datetimeVencimento) : '',
                    datetimePrevisao: res.datetimePrevisao ? new Date(res.datetimePrevisao) : '',
                    descricao: res.descricao || '',
                    idFormaRecebimento: res.idFormaRecebimento,
                    idContaFinanceira: res.idContaFinanceira,
                    valorJurosPrevisto: res.valorJurosPrevisto || 0,
                    valorMultaPrevisto: res.valorMultaPrevisto || 0,
                    valorDescontoPrevisto: res.valorDescontoPrevisto || 0,
                    valorAcrescimoPrevisto: res.valorAcrescimoPrevisto || 0,
                    valorTaxaIntermediadorPrevisto: res.valorTaxaIntermediadorPrevisto || 0,
                    valorTarifaIntermediadorPrevisto: res.valorTarifaIntermediadorPrevisto || 0,
                    observacoesHtml: res.observacoesHtml || '',
                    nsuTid: res.nsuTid || '',
                    tags: res.tags ?? []
                });

                // Store original financial values for PIX change detection
                this._originalValor = res.valor ?? null;
                this._originalJurosPrevisto = res.valorJurosPrevisto ?? null;
                this._originalMultaPrevisto = res.valorMultaPrevisto ?? null;
                this._originalAcrescimoPrevisto = res.valorAcrescimoPrevisto ?? null;
                this._originalIdContaFinanceira = res.idContaFinanceira ?? null;

                // Desabilitar vencimento se tem Recebimento parcial
                if (!this.podeEditarVencimento) {
                    this.form.get('dataVencimento')?.disable();
                }
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show('Erro', 'Não foi possível carregar a parcela.', AlertType.error);
                this.router.navigate(['/financas/contas-receber']);
            }
        });
    }

    public salvar(): void {
        if (this.isSaving) return;

        if (!this.idContasReceberParcela) {
            this.enviarSalvar();
            return;
        }

        this.contasReceberService.getCobrancaAtivoInfo([this.idContasReceberParcela])
            .pipe(catchError(() => of(null)))
            .subscribe((resInfo) => {
                const info = resInfo?.itens?.[0];
                let msg = '';
                if (this.encargosAlterados() && info?.temPixAtivo)
                    msg += info.suportaCancelamentoPix
                        ? '<br><br>Esta parcela possui um PIX ativo que será <strong>cancelado automaticamente</strong> ao salvar.'
                        : '<br><br>Esta parcela possui um PIX ativo. O banco não suporta cancelamento automático — <strong>cancele manualmente no seu banco</strong> após salvar.';
                if (info?.boletoJaPago)
                    msg += '<br><br>Esta parcela possui um <strong>boleto já pago</strong> — ele não será alterado; trate manualmente se necessário.';
                else if (info?.temBoletoAtivo)
                    msg += '<br><br>Esta parcela possui um <strong>boleto ativo</strong>. Ao salvar, ele será <strong>cancelado/regerado</strong> conforme a alteração.';

                if (msg) {
                    this.dialog.open(ConfirmModalComponent, {
                        data: {
                            title: 'Confirmar alteração',
                            innerHtml: msg + '<br><br>Deseja prosseguir?',
                            buttonConfirm: 'Confirmar',
                            buttonClose: 'Cancelar'
                        }
                    }).afterClosed().subscribe((res) => {
                        if (res?.confirm) this.enviarSalvar();
                    });
                } else {
                    this.enviarSalvar();
                }
            });
    }

    private enviarSalvar(): void {
        this.isSaving = true;

        if (!this.idPessoaCliente) {
            this.executarUpdate(null);
            return;
        }

        this.pessoaService.getById(this.idPessoaCliente).subscribe({
            next: pessoaResp => {
                if (pessoaResp.isError || !pessoaResp.pessoa) {
                    this.isSaving = false;
                    this.alertService.show('Erro', pessoaResp.errorDescription || 'Não foi possível obter os dados do cliente.', AlertType.error);
                    return;
                }

                const enderecos = pessoaResp.enderecos ?? [];
                const endereco = enderecos.find(item => item.isCobranca)
                    ?? enderecos.find(item => item.isPrincipal)
                    ?? enderecos[0];
                const idMunicipio = endereco?.idMunicipio;
                if (!idMunicipio) {
                    this.isSaving = false;
                    this.alertService.show('Erro', 'Cliente sem município no endereço de cobrança ou principal.', AlertType.error);
                    return;
                }

                this.municipioService.getByIds([idMunicipio]).subscribe({
                    next: municipioResponse => {
                        const municipio = municipioResponse.list?.find(item => item.idMunicipio === idMunicipio);
                        if (municipioResponse.isError || !municipio) {
                            this.isSaving = false;
                            this.alertService.show('Erro', municipioResponse.errorDescription || 'Não foi possível resolver o município do cliente.', AlertType.error);
                            return;
                        }
                        this.executarUpdate(SacadoBoletoStruct.fromPessoaResponse(pessoaResp, municipio));
                    },
                    error: () => {
                        this.isSaving = false;
                        this.alertService.show('Erro', 'Não foi possível consultar o município do cliente.', AlertType.error);
                    }
                });
            },
            error: () => {
                this.isSaving = false;
                this.alertService.show('Erro', 'Não foi possível obter os dados do cliente.', AlertType.error);
            }
        });
    }

    private executarUpdate(sacado: SacadoBoletoStruct | null): void {
        const f = this.form.getRawValue();

        const request: any = {
            idContasReceberParcela: this.idContasReceberParcela,
            valor: f.valor,
            dataVencimento: f.dataVencimento ? new Date(f.dataVencimento).toISOString() : null,
            datetimePrevisao: f.datetimePrevisao ? new Date(f.datetimePrevisao).toISOString() : null,
            descricao: f.descricao,
            idFormaRecebimento: f.idFormaRecebimento,
            idContaFinanceira: f.idContaFinanceira,
            valorJurosPrevisto: f.valorJurosPrevisto,
            valorMultaPrevisto: f.valorMultaPrevisto,
            valorDescontoPrevisto: f.valorDescontoPrevisto,
            valorAcrescimoPrevisto: f.valorAcrescimoPrevisto,
            observacoesHtml: f.observacoesHtml,
            nsuTid: f.nsuTid,
            tags: f.tags ?? [],
            sacado: sacado,
            anexos: this.anexos.map(a => ({
                idContasReceberParcelaAnexo: a.idContasReceberParcelaAnexo || null,
                nomeArquivo: a.nomeArquivo,
                tamanhoBytes: a.tamanhoBytes,
                base64: a.base64 || null
            }))
        };

        this.contasReceberParcelaService.update(request).subscribe({
            next: (res: any) => {
                this.isSaving = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Erro ao atualizar a parcela.', AlertType.error);
                    return;
                }
                if (res.avisoDescricao) {
                    const pixHtml = res.avisoDescricao.split(' | ').join('<br><br>');
                    this.dialog.open(ConfirmModalComponent, {
                        data: { title: 'Atenção — Cobrança', innerHtml: pixHtml, buttonClose: 'Entendi' }
                    });
                }
                this.alertService.show('Sucesso', 'Parcela atualizada com sucesso!', AlertType.success);
                this.router.navigate(this.rotaVoltar);
            },
            error: () => {
                this.isSaving = false;
                this.alertService.show('Erro', 'Ocorreu um erro ao comunicar-se com o servidor.', AlertType.error);
            }
        });
    }

    public cancelar(): void {
        this.router.navigate(this.rotaVoltar);
    }

    // ─── Upload / Download ───────────────────────────────────────────────

    public toggleHover(event: boolean): void {
        this.isHovering = event;
    }

    public onDrop(event: DragEvent): void {
        event.preventDefault();
        this.isHovering = false;
        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            this.processFiles(files);
        }
    }

    public onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isHovering = true;
    }

    public onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isHovering = false;
    }

    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.processFiles(input.files);
            input.value = '';
        }
    }

    private processFiles(files: FileList): void {
        const currentTotalSize = this.anexos.reduce((total, f) => total + f.tamanhoBytes, 0);
        let newFilesSize = 0;
        for (let i = 0; i < files.length; i++) {
            newFilesSize += files[i].size;
        }
        if (currentTotalSize + newFilesSize > this.MAX_TOTAL_SIZE) {
            this.alertService.show('Aviso', 'O tamanho total dos anexos não pode exceder 10MB.', AlertType.warning);
            return;
        }
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (file.size > this.MAX_FILE_SIZE) {
                this.alertService.show('Aviso', `O arquivo "${file.name}" excede o limite de 2MB.`, AlertType.warning);
                continue;
            }
            const reader = new FileReader();
            reader.onload = (e: any) => {
                const base64Data = e.target.result.split(',')[1];
                this.anexos.push({
                    nomeArquivo: file.name,
                    tamanhoBytes: file.size,
                    base64: base64Data
                });
            };
            reader.readAsDataURL(file);
        }
    }

    public removeAnexo(index: number): void {
        this.anexos.splice(index, 1);
    }

    public limparAnexos(): void {
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmação',
                description: 'Deseja realmente remover todos os anexos?',
                buttonConfirm: 'Sim',
                buttonClose: 'Não'
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result && result.confirm) {
                this.anexos = [];
            }
        });
    }

    public formatBytes(bytes: number, decimals: number = 2): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }

    public downloadAnexo(index: number): void {
        const anexo = this.anexos[index];
        if (!anexo.urlArquivo) {
            this.alertService.show('Aviso', 'Este arquivo ainda não foi salvo no servidor.', AlertType.warning);
            return;
        }
        this.contasReceberParcelaService.getArquivoContasReceberParcela(anexo.urlArquivo).subscribe({
            next: (res: any) => {
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Erro ao baixar o arquivo.', AlertType.error);
                    return;
                }
                const a = document.createElement('a');
                a.href = res.urlArquivo;
                a.download = anexo.nomeArquivo;
                a.target = '_blank';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            },
            error: () => {
                this.alertService.show('Erro', 'Erro ao baixar o arquivo.', AlertType.error);
            }
        });
    }

    onDateInput(event: Event, controlName: string): void {
        MaskService.onDateInput(event, this.form.get(controlName));
    }

    private encargosAlterados(): boolean {
        const f = this.form.getRawValue();
        return f.valor !== this._originalValor
            || f.valorJurosPrevisto !== this._originalJurosPrevisto
            || f.valorMultaPrevisto !== this._originalMultaPrevisto
            || f.valorAcrescimoPrevisto !== this._originalAcrescimoPrevisto
            || f.idContaFinanceira !== this._originalIdContaFinanceira;
    }
}
